/**
 * NAOKO Intent Collector
 * Tracks click signals on PDP: size variants, thumbnails, color swatches.
 * Builds a confidence score (0–1) used by recommendation-widget.js for Variant B.
 *
 * API: window.naokoIntent
 *   .color      {string|null}
 *   .size       {string|null}
 *   .style      {string|null}  — set externally by archetype-detector.js
 *   .confidence {number}       — 0–1, +0.25 per new unique signal type
 *   .addSignal(type, value)    — type: 'color'|'size'|'style'
 *   .getIntent()               — returns plain object snapshot
 *
 * Signals survive navigation within the same session via sessionStorage.
 * All errors are silently caught — widget works without this file.
 */

(function () {
  'use strict';

  var SESSION_KEY = 'naoko_intent';

  // ── State ────────────────────────────────────────────────────────────────

  var state = {
    color:      null,
    size:       null,
    style:      null,
    confidence: 0,
    _seen:      {}   // which signal types have already contributed to confidence
  };

  // ── sessionStorage helpers ───────────────────────────────────────────────

  function loadFromSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (saved.color)      state.color      = saved.color;
      if (saved.size)       state.size       = saved.size;
      if (saved.style)      state.style      = saved.style;
      if (saved.confidence) state.confidence = saved.confidence;
      if (saved._seen)      state._seen      = saved._seen;
    } catch (e) {
      // sessionStorage unavailable or parse error — start fresh
    }
  }

  function saveToSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        color:      state.color,
        size:       state.size,
        style:      state.style,
        confidence: state.confidence,
        _seen:      state._seen
      }));
    } catch (e) {
      // sessionStorage unavailable (private browsing) — continue in-memory only
    }
  }

  // ── addSignal ─────────────────────────────────────────────────────────────
  // Each unique signal type contributes +0.25 to confidence (max 1.0).
  // Subsequent signals of the same type update the value but not confidence.

  function addSignal(type, value) {
    if (!type || !value) return;
    if (type !== 'color' && type !== 'size' && type !== 'style') return;

    var normalised = String(value).trim().toLowerCase();
    if (!normalised) return;

    // Update stored value for this type
    state[type] = normalised;

    // Increase confidence only on first signal of each type
    if (!state._seen[type]) {
      state._seen[type] = true;
      state.confidence  = Math.min(1, state.confidence + 0.25);
    }

    saveToSession();

    // Notify archetype-detector synchronously after every signal
    // Detector may not be loaded yet (defer order) — guard with typeof check
    if (typeof window.naokoDetect === 'function') {
      window.naokoDetect();
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  window.naokoIntent = {
    get color()      { return state.color; },
    get size()       { return state.size; },
    get style()      { return state.style; },
    set style(v)     {
      state.style = v;
      // style set by archetype-detector counts as a signal if not already seen
      if (v && !state._seen['style']) {
        state._seen['style'] = true;
        state.confidence = Math.min(1, state.confidence + 0.25);
      }
      saveToSession();
    },
    get confidence() { return state.confidence; },

    addSignal: addSignal,

    getIntent: function () {
      return {
        color:      state.color,
        size:       state.size,
        style:      state.style,
        confidence: state.confidence
      };
    }
  };

  // ── Signal extraction helpers ─────────────────────────────────────────────

  function extractColor(el) {
    // Prefer explicit data-color attribute
    var c = el.dataset && el.dataset.color;
    if (c) return c;

    // Check alt text / aria-label for a recognisable Polish color keyword
    var text = (el.alt || el.getAttribute('aria-label') || el.title || '').toLowerCase();
    var COLORS = [
      'czarny','czarna','bialy','biala','szary','szara','bezowy','bezowa',
      'rozowy','rozowa','czerwony','czerwona','granatowy','granatowa',
      'niebieski','niebieska','zielony','zielona','bordowy','bordowa',
      'lila','kremowy','kremowa','zolty','zolta','pomaranczowy','pomaranczowa',
      'wielokolorowy','ecru','turkusowy','turkusowa'
    ];
    for (var i = 0; i < COLORS.length; i++) {
      if (text.indexOf(COLORS[i]) !== -1) return COLORS[i];
    }
    return null;
  }

  function extractSize(el) {
    // input[type=radio] or hidden input with value like "S/M", "XL"
    var val = el.value || el.dataset.value || el.textContent || '';
    val = val.trim();
    if (!val) return null;
    // Accept anything that looks like a size token
    if (/^(XXS\/XS|XS\/S|S\/M|M\/L|L\/XL|XL\/XXL|XXXL|XXL|XXS|XL|XS|ONE SIZE|S|M|L|\d{2,3})\b/i.test(val)) {
      return val.toUpperCase();
    }
    return null;
  }

  // ── Event listeners ───────────────────────────────────────────────────────
  // Use capture: true so we intercept before theme handlers stop propagation.

  function onDocumentClick(e) {
    try {
      var target = e.target;
      if (!target) return;

      // ── Size variant click ──────────────────────────────────────────────
      // Targets: radio inputs directly, OR labels pointing to radio inputs.
      // ella7 theme: user clicks <label for="id">, not the <input> itself.
      //
      // We intentionally do NOT match on name="Size" or name*="Rozmiar" —
      // Shopify translates the option name into the store language, so the
      // attribute changes per locale (Size / Rozmiar / Größe / Taille…).
      // Instead we match the stable parent classes ella7 always renders:
      //   variant-selects  —  the custom element wrapping all variant pickers
      //   fieldset.js      —  each option group inside variant-selects
      //   .variant-input-wrap, input[data-option]  —  other common theme patterns
      var sizeEl = target.closest(
        'variant-selects input[type="radio"], ' +
        'fieldset.js input[type="radio"], ' +
        '.variant-input-wrap input, ' +
        'input[data-option]'
      );
      // If user clicked a <label for="..."> resolve to its associated input
      // (ella7 wraps inputs in labels — the visible click target is the label)
      if (!sizeEl) {
        var labelEl = target.closest('label[for]');
        if (labelEl) {
          var forInput = document.getElementById(labelEl.htmlFor);
          if (forInput && forInput.type === 'radio') sizeEl = forInput;
        }
      }
      if (sizeEl) {
        var sizeVal = extractSize(sizeEl);
        if (sizeVal) {
          addSignal('size', sizeVal);
          return;
        }
      }

      // ── Thumbnail / media click → style signal via color ────────────────
      var thumbEl = target.closest('.product__media-item, .thumbnail-list__item, .product-single__thumbnail');
      if (thumbEl) {
        var img = thumbEl.querySelector('img');
        if (img) {
          var color = extractColor(img);
          if (color) {
            addSignal('color', color);
            return;
          }
        }
      }

      // ── Color swatch click ───────────────────────────────────────────────
      var swatchEl = target.closest('[data-color], .color-swatch, .swatch__item--color');
      if (swatchEl) {
        var swatchColor = extractColor(swatchEl) || swatchEl.dataset.color;
        if (swatchColor) {
          addSignal('color', swatchColor);
        }
      }

    } catch (e2) {
      // Silently ignore — never break user interaction
    }
  }

  // ── MutationObserver — re-attach if theme re-renders variant selectors ──

  function observeDom() {
    try {
      var observer = new MutationObserver(function () {
        // Nothing to re-attach — we use document-level capture listener.
        // Observer is here to detect dynamic page loads (theme SPAs).
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      // MutationObserver unavailable — static listener still works
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  loadFromSession();
  document.addEventListener('click', onDocumentClick, true);
  observeDom();

})();
